import { Box, Button, Flex, Grid, Input, Text, Select, Badge, Tooltip } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { _localization } from "../../../config/location";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import Repository from "../../../_base/helper/HttpHelper";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { API_ROUTE } from "../../../const/apiRoute";
import { useLocation } from "react-router-dom";

type CustomerComplaint = {
  id: number;
  cusName: string | null;
  email: string | null;
  phoneNumber: string | null;
  content: string | null;
  createdAt: string | null;
  active: boolean | null;
};

const CustomerComplaintList = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [height, setHeight] = useState(0);
  const [data, setData] = useState<CustomerComplaint[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const sizeGrid = { base: 12, sm: 6, md: 4, lg: 2, xl: 2 };
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";

  const formSeachInput = useForm({
    mode: "uncontrolled",
    initialValues: {
      KeySearch: _searchJson && checkedPinSearch ? _searchJson?.KeySearch : "",
      CusName: _searchJson && checkedPinSearch ? _searchJson?.CusName : "",
      Email: _searchJson && checkedPinSearch ? _searchJson?.Email : "",
      PhoneNumber: _searchJson && checkedPinSearch ? _searchJson?.PhoneNumber : "",
      Active: _searchJson && checkedPinSearch ? _searchJson?.Active : "",
    },
  });

  const columns = React.useMemo<MRT_ColumnDef<CustomerComplaint>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        enableColumnActions: false,
        enableSorting: false,
        enableResizing: false,
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 50,
      },
      {
        accessorKey: "cusName",
        header: "Họ tên",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "phoneNumber",
        header: "Số điện thoại",
        size: 150,
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 250,
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "content",
        header: "Nội dung thắc mắc, góp ý",
        size: 600,
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>() || "N/A";
          return (
            <Tooltip label="Click để xem đầy đủ" position="top" withArrow>
              <Text
                size="sm"
                style={{ cursor: "pointer" }}
                lineClamp={2}
                onClick={() => {
                  modals.open({
                    title: <b style={{ fontSize: "16px" }}>Chi tiết thắc mắc, góp ý</b>,
                    children: (
                      <Box p="xs" style={{ minWidth: "450px", maxWidth: "700px" }}>
                        <Text size="sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#373a40" }}>
                          {val}
                        </Text>
                      </Box>
                    ),
                    centered: true,
                    size: "auto",
                  });
                }}
              >
                {val}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Ngày gửi",
        size: 150,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
    ],
    []
  );

  const fetchData = async () => {
    setIsLoading(true);
    setData([]);
    setIsError(false);

    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      const queryParams = new URLSearchParams();

      queryParams.append(
        "Skip",
        String(pagination.pageIndex * pagination.pageSize)
      );
      queryParams.append("Take", String(pagination.pageSize));

      const searchValues = formSeachInput.getValues();
      if (searchValues.KeySearch?.trim()) {
        queryParams.append("KeySearch", searchValues.KeySearch);
      }
      if (searchValues.CusName?.trim()) {
        queryParams.append("CusName", searchValues.CusName);
      }
      if (searchValues.Email?.trim()) {
        queryParams.append("Email", searchValues.Email);
      }
      if (searchValues.PhoneNumber?.trim()) {
        queryParams.append("PhoneNumber", searchValues.PhoneNumber);
      }
      if (searchValues.Active) {
        queryParams.append("Active", searchValues.Active);
      }

      const url = `${API_ROUTE.CUSTOMER_COMPLAINT}?${queryParams.toString()}`;
      const response = await repository.get(url);

      if (response && response.data) {
        setData(response.data);
        setRowCount(response.totalCount || 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching customer complaints:", error);
      setIsError(true);
      NotificationExtension.Warn("Có lỗi xảy ra khi tải dữ liệu!");
    } finally {
      setIsLoading(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify(formSeachInput.getValues())
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageIndex, pagination.pageSize]);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["STT"],
        right: ["active"],
      },
      columnVisibility: { id: true },
      density: "xs",
    },
    enableRowSelection: false,
    mantineTableContainerProps: {
      style: { maxHeight: height - 100, minHeight: height - 100 },
    },
    enableStickyHeader: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: () => ({
      style: {
        fontWeight: "550",
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      pagination,
      isLoading,
      showAlertBanner: isError,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (130 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [pagination]);

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        style={{
          border: "1px solid #dee2e6",
          padding: "5px 10px",
        }}
      >
        <BreadCrumb />
      </Flex>
      <Box ref={headerRef}>
        <Grid style={{ width: "100%" }} mt={10} gutter="xs">
          <Grid.Col span={sizeGrid}>
            <Input
              size="sm"
              placeholder="Từ khoá"
              type="text"
              leftSection={<IconSearch color="#15aabf" size={16} />}
              key={formSeachInput.key("KeySearch")}
              {...formSeachInput.getInputProps("KeySearch")}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Input
              size="sm"
              placeholder="Tên khách hàng"
              type="text"
              leftSection={<IconSearch color="#15aabf" size={16} />}
              key={formSeachInput.key("CusName")}
              {...formSeachInput.getInputProps("CusName")}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Input
              size="sm"
              placeholder="Số điện thoại"
              type="text"
              leftSection={<IconSearch color="#15aabf" size={16} />}
              key={formSeachInput.key("PhoneNumber")}
              {...formSeachInput.getInputProps("PhoneNumber")}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Input
              size="sm"
              placeholder="Email"
              type="text"
              leftSection={<IconSearch color="#15aabf" size={16} />}
              key={formSeachInput.key("Email")}
              {...formSeachInput.getInputProps("Email")}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Button
              type="submit"
              leftSection={<IconSearch size={16} />}
              color="blue"
              variant="outline"
              fullWidth
              onClick={async () => {
                await fetchData();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </Box>

      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </>
  );
};

export default CustomerComplaintList;
